'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { CatTextLink } from 'blocks-nextjs/blocks-react/components/TextLink';
import {
  Conditional,
  DealerErrorNotification,
  DealerLocatorTermsCheckbox
} from '@cat-ecom/pcc-components';
import useGetDealerInfo from 'components/dealer-locator/hooks/use-get-dealer-info';
import useReplaceTokensWithElements from 'hooks/use-replace-tokens-with-elements';
import { CAT_LEGAL_TERMS_FLAG } from 'constants/featureFlag';
import { getCatOrDealerLegalURL, getRelativeURLWithParams } from 'utils/url';
import { TERMS_LINKS, CONTACT_DEALER_LINK } from 'constants/links';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { useBreakpoint } from 'components/pcc-components';
import { useDealerLocatorDrawer } from 'components/dealer-locator/dealer-locator-drawer-context';
import { CatIconExternalLinkBox } from 'blocks-nextjs/blocks-react/components/Icons/ExternalLinkBox';
import cx from 'classnames';
import { v4 as uuid } from 'uuid';
import { SelectStoreResponse } from 'services/dealer-locator/types';
import { normalizeCommerceLink } from 'utils/links';
import { useEsiteCtx } from 'services/esite/context';
interface DealerLocatorTermsWrapperProps {
  dealerName: string;
  isDisabled?: boolean;
  storeId: string;
  isSelectStoreError?: boolean;
  selectStoreState?: SelectStoreResponse;
  handleShopAsGuest?: () => void;
}

export const DealerLocatorTermsWrapper = ({
  dealerName,
  isDisabled,
  storeId,
  isSelectStoreError,
  selectStoreState,
  handleShopAsGuest
}: DealerLocatorTermsWrapperProps) => {
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es').
  const { lang }: esiteParams = useParams();
  const t = useTranslations();
  const { catalogId, langId } = useEsiteCtx();
  const dealerLocatorDrawer = useDealerLocatorDrawer();
  const replaceTokens = useReplaceTokensWithElements();
  const { getDealerInfo, data, isError, isLoading } = useGetDealerInfo();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const isStoreLocationTandCFlag =
    dealerLocatorDrawer?.isStoreLocationTandCFlag ?? false;
  const shouldShowIcon = isStoreLocationTandCFlag && !isMD;
  const termsOfUseKey = 'DEALERS_TERMS_OF_USE';
  const privacyKey = 'UR_CAT_DEALER_PRIVACY_NOTICE_HREF';
  const areLinksDisabled = isError || isLoading || !data?.seoURL;
  const catLegalTermsFlagValue =
    data?.featureFlags?.[CAT_LEGAL_TERMS_FLAG] ?? false;

  useEffect(() => {
    getDealerInfo(storeId);
  }, [getDealerInfo, storeId]);

  const getDealerName = () => <span data-testid="dealer">{dealerName}</span>;

  const privacyNoticeURL = catLegalTermsFlagValue
    ? getCatOrDealerLegalURL(TERMS_LINKS.CAT_PRIVACY_NOTICE_LINK, lang, true)
    : getCatOrDealerLegalURL(TERMS_LINKS.CAT_PRIVACY_NOTICE, lang, true);
  const legalTermsURL = catLegalTermsFlagValue
    ? getCatOrDealerLegalURL(TERMS_LINKS.CAT_LEGAL_TERMS_LINK, lang, true)
    : getCatOrDealerLegalURL(TERMS_LINKS.CAT_LEGAL_TERMS_OF_USE, lang, true);

  const errorCodeMessage = selectStoreState?.errorCode
    ? ` ${t('MLP_ERROR4', { 0: selectStoreState.errorCode })}`
    : '';

  const contactDealerLink = normalizeCommerceLink(
    getRelativeURLWithParams(CONTACT_DEALER_LINK, {
      catalogId,
      langId,
      storeId
    })
  );

  const termLinksValues = [
    <CatTextLink
      href={legalTermsURL}
      key="UR_PARTS_TERMS_OF_USE_HREF"
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      {t('UR_PARTS_TERMS_OF_USE_HREF')}
      {shouldShowIcon && (
        <div className="d-flex align-items-center ms-2">
          <CatIconExternalLinkBox />
        </div>
      )}
    </CatTextLink>,
    <CatTextLink
      href={privacyNoticeURL}
      key="UR_CAT_PRIVACY_NOTICE_HREF"
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      {t('UR_CAT_PRIVACY_NOTICE_HREF')}
      {shouldShowIcon && (
        <div className="d-flex align-items-center ms-2">
          <CatIconExternalLinkBox />
        </div>
      )}
    </CatTextLink>,
    <CatTextLink
      href={
        !areLinksDisabled
          ? getCatOrDealerLegalURL(
              TERMS_LINKS.DEALER_TERMS,
              lang,
              false,
              data?.seoURL
            )
          : undefined
      }
      key={termsOfUseKey}
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      <span
        className={cx({
          'text-start': isStoreLocationTandCFlag && !isMD
        })}
      >
        {replaceTokens(`${t(termsOfUseKey, { 0: '{0}' })} `, [getDealerName()])}
      </span>
      {shouldShowIcon && (
        <div className="d-flex align-items-center ms-2">
          <CatIconExternalLinkBox />
        </div>
      )}
    </CatTextLink>,
    <CatTextLink
      href={
        !areLinksDisabled
          ? getCatOrDealerLegalURL(
              TERMS_LINKS.DEALER_PRIVACY_POLICY,
              lang,
              false,
              data?.seoURL
            )
          : undefined
      }
      key={privacyKey}
      rel="noreferrer"
      size="sm"
      target="_blank"
    >
      <span
        className={cx({
          'text-start': isStoreLocationTandCFlag && !isMD
        })}
      >
        {replaceTokens(`${t(privacyKey, { 0: '{0}' })} `, [getDealerName()])}
      </span>
      {shouldShowIcon && (
        <div className="d-flex align-items-center ms-2">
          <CatIconExternalLinkBox />
        </div>
      )}
    </CatTextLink>
  ];
  // Replace tokens in translation with more tokens, to be replaced with Elements, since translation function demands values for tokens in translation but does not accept Elements
  const termLinks = replaceTokens(
    t('UR_CAT_ACCEPT_TERMS', { 0: '{0}', 1: '{1}', 2: '{2}', 3: '{3}' }),
    termLinksValues
  );

  const getTitle = () => {
    const title = t('UNKNOWN_ERROR_TITLE');
    return `${title[0].toUpperCase()}${title.slice(1).toLowerCase()}`;
  };

  const errorMessage = `${t('UNKNOWN_ERROR_MESSAGE')}${errorCodeMessage ?? ''}`;

  return (
    <Conditional
      test={!(isStoreLocationTandCFlag && !isMD)}
      fallback={
        <div className="d-flex flex-column gap-3 align-items-start">
          <p className="cat-u-theme-typography-body  w-auto class= m-0 text-start">
            {t('T_AND_C_TERMS_PRIVACY_POLICY')}
          </p>
          <Conditional test={isSelectStoreError}>
            <DealerErrorNotification
              title={getTitle()}
              errorMessage={errorMessage}
              contactDealerLink={contactDealerLink}
              contactUsLabel={t('FOOTER_CONTACT_US')}
              handleShopAsGuest={handleShopAsGuest}
              shopAsGuestLabel={t('SHOP_AS_GUEST')}
            />
          </Conditional>
          {termLinksValues?.map((item, index) => (
            <div key={uuid()}>{item}</div>
          ))}
        </div>
      }
    >
      <DealerLocatorTermsCheckbox
        title={t('1628_ACCEPT_TERMS_AND_CONDITIONS_2').replace('.', '')}
        label={` (${t('REQUIRED').toLowerCase()})`}
        isDisabled={isDisabled}
      >
        {termLinks}
      </DealerLocatorTermsCheckbox>
    </Conditional>
  );
};
